import {
  AfterViewChecked,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import { SubSink } from 'subsink';

import {
  BreadcrumbItem,
  BreadcrumbItems,
} from '@shared/interfaces/breadcrumb-item.interface';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
})
export class BreadcrumbComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('innerItemsOpener') innerItemsOpener?: ElementRef;
  @Input() contentOnCard?: boolean;

  subs = new SubSink();
  breadcrumbItems: Array<BreadcrumbItem> = [];

  breadcrumbItemsStructure = flatten(BreadcrumbItems);

  breadCrumbFullWidth = 0;
  containerWidth = 0;

  isInnerItemsOpened = false;

  queryParam?: Params;

  showBread: 'NORMAL' | 'MOBILE' | undefined = undefined;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {
    this.renderer.listen('window', 'click', (e: Event) => {
      if (e.target !== this.innerItemsOpener?.nativeElement) {
        this.isInnerItemsOpened = false;
      }
    });
  }

  ngOnInit(): void {
    this.updateBreadcrumbItems();

    this.subs.add(
      this.router.events.subscribe((e) => {
        if (e instanceof NavigationEnd) {
          this.updateBreadcrumbItems();
        }
      })
    );

    this.subs.add(
      this.route.queryParams.subscribe((params) => {
        this.queryParam = params;
      })
    );
  }

  ngAfterViewChecked(): void {
    const breadcrumb = document.getElementById('breadcrumb');
    if (
      this.breadCrumbFullWidth !== breadcrumb?.clientWidth ||
      this.containerWidth !== this.elRef.nativeElement.offsetParent.clientWidth
    ) {
      this.breadCrumbFullWidth = breadcrumb?.clientWidth ?? 0;
      this.containerWidth =
        this.elRef.nativeElement.offsetParent.clientWidth ?? 0;
      this.showBread =
        (breadcrumb?.clientWidth ?? 0) <
        this.elRef.nativeElement.offsetParent.clientWidth - 100
          ? 'NORMAL'
          : 'MOBILE';
    }
  }

  updateBreadcrumbItems() {
    const segments = this.router.url.split('/').map((segment) => {
      const index = segment.indexOf('?');
      if (index >= 0) {
        const slicedSegment = segment.slice(0, index);
        let queryParams = localStorage.getItem('queryParams');
        if (queryParams) {
          let parsedQueries = JSON.parse(queryParams);
          parsedQueries[slicedSegment] = this.queryParam;
          localStorage.setItem('queryParams', JSON.stringify(parsedQueries));
        } else {
          const newQueries = { [slicedSegment]: this.queryParam };
          localStorage.setItem('queryParams', JSON.stringify(newQueries));
        }
        return slicedSegment;
      } else {
        return segment;
      }
    });
    this.breadcrumbItems = segments
      .map((segment, index) => {
        const url = segments.slice(0, index + 1).join('/');
        const sideNavItem = this.breadcrumbItemsStructure.find(
          (navItem) => navItem.path && navItem.path === url
        );
        if (
          sideNavItem &&
          (!sideNavItem.showOnlyIfNotLast ||
            (sideNavItem.showOnlyIfNotLast && index !== segments.length - 1))
        ) {
          return {
            title: sideNavItem.title,
            path: sideNavItem.path,
            queryParams: this.findQuery(segment),
          };
        } else {
          return { title: segment, path: '' };
        }
      })
      .filter((segments) => !!segments.path && segments.path !== '/home');
    this.breadcrumbItems.unshift({
      title: 'MENU.HOME',
      path: '/home',
    });
  }

  findQuery(segment: string) {
    let queryParams = localStorage.getItem('queryParams');
    if (queryParams) {
      return JSON.parse(queryParams)[segment];
    } else {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

export function flatten(routes: Array<BreadcrumbItem>): Array<BreadcrumbItem> {
  return routes.reduce<Array<BreadcrumbItem>>((accumulator, current) => {
    const { children, ...rest } = current;
    const flattenedChildren = children ? flatten(children) : [];

    return [...accumulator, rest, ...flattenedChildren];
  }, []);
}
